/**
 * Shared series styling for the four Recharts area charts (POKE-INDEX, PoC,
 * watches, city indices).
 *
 * The four charts are the same object drawn four times, so the things that make
 * the series read as a price line — direction colour, stroke weight, the glow
 * under it and the dot on its last point — live here rather than being retyped
 * per file and drifting apart.
 *
 * Direction, not category: a price line's colour is the one piece of
 * information a reader takes from it before reading any axis, so the series is
 * green when the visible range closes above where it opened and red when it
 * closes below. The category accent still owns the chrome around the chart —
 * the timeframe pill, the card glow — which is where an identity colour belongs.
 */

/** Green/red plus the matching glow for the visible range's direction. */
export const trendStyle = (rows, dataKey = "price") => {
  const first = Number(rows?.[0]?.[dataKey]);
  const last = Number(rows?.[rows.length - 1]?.[dataKey]);
  const known = Number.isFinite(first) && Number.isFinite(last);
  const up = !known || last >= first;

  return {
    up,
    /* Flat when there is nothing to compare against, so a single-point series
       does not claim a direction it does not have. */
    color: up ? "var(--up, #1F8B58)" : "var(--down, #C8353A)",
    glow: up ? "rgba(31, 139, 88, .26)" : "rgba(200, 53, 58, .26)",
    lastValue: known ? last : null,
  };
};

/**
 * The dot that marks where the series ends — the one point worth marking on a
 * line whose right edge is "now". Recharts clones this for every point, so it
 * draws nothing until it is handed the last one.
 */
export const EndDot = ({ cx, cy, index, lastIndex, color }) => {
  if (index !== lastIndex || cx == null || cy == null) return null;
  return (
    <g>
      {/* Halo, so the dot stays visible over the fill beneath it. */}
      <circle cx={cx} cy={cy} r={8.5} fill={color} opacity={0.18} />
      <circle
        cx={cx}
        cy={cy}
        r={4.5}
        fill={color}
        stroke="var(--surface, #FFFFFF)"
        strokeWidth={2.5}
      />
    </g>
  );
};
